import { useCallback, useEffect, useMemo } from 'react';
import { useMergedConfig } from '../context/MergedConfigProvider';
import { subscribeToPCoreMessage } from '../utils/pCore';

// ============================================================================
// Types
// ============================================================================

interface UserData {
  personId?: string;
  email?: string;
  usedAddressFinder?: boolean;
  demoAccountID?: string;
  caseID?: string;
  tradingType?: string;
  applicationID?: string;
  isABTestScenario?: boolean;
}

interface MParticleUser {
  getMPID(): string;
  setUserAttribute(key: string, value: string | number | boolean): void;
}

interface MParticleIdentityResult {
  httpCode: number;
  getUser(): MParticleUser | null;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULTS = {
  COUNTRY: 'gb',
  LANGUAGE: 'en',
  CHANNEL: 'web',
  NATURE: 'individual',
  TRADING_INTENT: 'live',
  SUBCATEGORY: 'form',
} as const;

// ============================================================================
// Shared State
// ============================================================================

let userData: UserData = {};
let applicationIDDeferred: { resolve: (value: string) => void; timeoutId?: NodeJS.Timeout } | null = null;
let demoAppSubmitLoggedForPersonId: string | null = null;
let lastIdentifiedPersonId: string | null = null;
let tradingTypeLocked = false;

let demoRegistrationFallbackTimeout: ReturnType<typeof setTimeout> | null = null;

// Subscribe to AB testing at module level so we never miss the early-firing event
(function subscribeToABTestingWhenReady() {
  let abTestSubscription: { unsubscribe: () => void } | undefined;
  const interval = setInterval(() => {
    if (abTestSubscription) {
      clearInterval(interval);
      return;
    }
    const sub = subscribeToPCoreMessage<{ caseID?: string; IsABTestScenario?: boolean }>(
      'CMC_AB_TESTING_DETAILS',
      (event) => {
        const { IsABTestScenario } = event.message;
        if (IsABTestScenario !== undefined) {
          userData = { ...userData, isABTestScenario: IsABTestScenario };
        }
      }
    );
    if (sub) {
      abTestSubscription = sub;
      clearInterval(interval);
    }
  }, 100);
  // No timeout - poll indefinitely until subscription succeeds
})();

// ============================================================================
// Helper Functions
// ============================================================================

function setUserAttributes(user: MParticleUser, personId: string, email?: string): void {
  user.setUserAttribute('person_id', personId);
  if (email) {
    user.setUserAttribute('email', email);
  }
}

function dispatchMobileEvent(
  eventName: string,
  payload: Record<string, unknown>,
  embedded: boolean,
): void {
  const mobileMessage = JSON.stringify({ data: payload, event: eventName });
  if (embedded) {
    window.parent.postMessage(mobileMessage, '*');
  } else {
    window.postMessage(mobileMessage, '*');
  }
  if (window.AnalyticsWebInterface) {
    window.AnalyticsWebInterface.logEvent(eventName, JSON.stringify(payload));
  } else if (window.webkit?.messageHandlers?.firebase) {
    window.webkit.messageHandlers.firebase.postMessage({
      command: 'logEvent',
      name: eventName,
      parameters: payload,
    });
  } else {
    // No native bridge (AnalyticsWebInterface / webkit) — postMessage was already sent above
  }
}

// ============================================================================
// Hook
// ============================================================================
export const useMParticle = () => {
  const { product, embedded = false, pegaStartingFields, platform, lang, region } = useMergedConfig();

  const context = pegaStartingFields?.Context; // "Live" or "Demo"
  const isDemo = context === 'Demo';
  const traderType = isDemo ? 'demo' : 'live';
  const hostingType = platform || pegaStartingFields?.Platform || '';
  const nature = pegaStartingFields?.CustomerType?.toLowerCase() === 'corporate' ? 'corporate' : 'individual';

 const productCode =
  (product?.toLowerCase() === "spectreretail") ? "SBR" :
  (product?.toLowerCase() === "spectre")
    ? "SBP"
    : (product?.toUpperCase() === "CFD")
      ? "CFD"
      : (product?.toUpperCase() === "SB")
        ? "SB"
        : (product || "Equity");

  const setUserData = useCallback((data: UserData) => {
    userData = { ...userData, ...data };

    try {
       if (window?.mParticle?.Identity && data.personId && typeof data.personId === 'string' && data.personId.trim()) {
        // Only call identify if personId is different from last identified one
        if (data.personId !== lastIdentifiedPersonId) {
          lastIdentifiedPersonId = data.personId;

          const identityRequest = {
            userIdentities: {
              customerid: data.personId,
              ...(data.email && { email: data.email })
            }
          };

          window.mParticle.Identity.identify(
            identityRequest,
            (result: MParticleIdentityResult) => {
              const user = result.getUser();
              if (user && user.getMPID()) {
                setUserAttributes(user, data.personId!, data.email);
              }
            }
          );
        }
      }
    } catch (error) {
      console.error('mParticle setUserData error:', error);
    }
  }, []);

  // Subscribe to Pega product messages (set tradingType for mParticle)
  useEffect(() => {
    const subscription = subscribeToPCoreMessage<{ product?: string }>(
      'CMC_CRM_CFD_PRODUCT_STARTED',
      (event) => {
        const { product: prod } = event.message || {};
        if (prod) {
          try {
            const p = String(prod || '').toUpperCase();
            if (p.includes('CFD')) {
              tradingTypeLocked = true;
              userData = { ...userData, tradingType: 'CFD' };
            } else if (p.includes('EQUITIES')) {
              tradingTypeLocked = true;
              userData = { ...userData, tradingType: 'Equity' };
            }
          } catch {
            // noop
          }
        }
      }
    );
    return () => {
      if (subscription && typeof (subscription).unsubscribe === 'function') {
        (subscription).unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    const subscription = subscribeToPCoreMessage<{ Product?: string }>(
      'CMC_CRM_SET_APP_CONTEXT',
      (event) => {
        const { Product: prod } = event.message || {};
        if (prod && !tradingTypeLocked) {
          const p = prod.toUpperCase();
          if (p.includes('CFD')) {
            userData = { ...userData, tradingType: 'CFD' };
          } else if (p.includes('SPECTRE')) {
            userData = { ...userData, tradingType: 'SBR' };
          } else if (p === 'SB' || p.includes('SPREAD')) {
            userData = { ...userData, tradingType: 'SB' };
          } else if (p.includes('EQUITY') || p.includes('EQUITIES')) {
            userData = { ...userData, tradingType: 'Equity' };
          }
          // Ignore unknown product values - don't store raw Pega names
        }
      }
    );
    return () => {
      if (subscription && typeof (subscription).unsubscribe === 'function') {
        (subscription).unsubscribe();
      }
    };
  }, []);

  const setTradingType = useCallback((type: string) => {
    userData = { ...userData, tradingType: type };
  }, []);

  const setUsedAddressFinder = useCallback((used: boolean) => {
    userData = { ...userData, usedAddressFinder: used };
  }, []);

  const buildPayload = useCallback((category: string, step: string, extraData: Record<string, unknown> = {}) => {
    return {
      'application.country_of_residence': DEFAULTS.COUNTRY,
      'application.nature': nature,
      'application.trading_intent': DEFAULTS.TRADING_INTENT,
      'application.step': step,
      'application.person_id': userData.personId || '',
      'application.email': userData.email || '',
      'application.hosting_type': hostingType,
      'page.formType': 'pega',
      'page.category': category,
      'page.subcategory': DEFAULTS.SUBCATEGORY,
      'page.channel': DEFAULTS.CHANNEL,
      'page.countryCode': DEFAULTS.COUNTRY,
      'page.language': DEFAULTS.LANGUAGE,
      'page.regionCode': `${DEFAULTS.LANGUAGE}_${DEFAULTS.COUNTRY}`,
      'Url': window.location.href.substring(0, 100),
      ...(userData.isABTestScenario !== undefined && { 'application.is_ab_test_scenario': userData.isABTestScenario }),
      ...extraData
    };
  }, [hostingType, nature]);

  const sendEvent = useCallback((eventName: string, payload: Record<string, unknown>, eventTypeKey: 'Other' | 'Navigation' = 'Other') => {
    try {
      const sendEventInternal = () => {
        if (!window.mParticleInstances || !window.mParticle) return;


        const firstInstance = window.mParticleInstances[0];
        if (firstInstance) {
          const instance = window.mParticle?.getInstance(firstInstance);
          if (instance && window.mParticle?.EventType) {
            instance.logEvent(
              eventName,
              window.mParticle.EventType[eventTypeKey],
              payload
            );
          }
        }
      };

      if (!window?.mParticle?.ready) {
        const checkInterval = setInterval(() => {
          if (window?.mParticle?.ready) {
            clearInterval(checkInterval);
            window.mParticle.ready(sendEventInternal);
          }
        }, 100);
        setTimeout(() => clearInterval(checkInterval), 5000);
        return;
      }

      window.mParticle.ready(sendEventInternal);
    } catch (error) { console.log(error)}
  }, []);

  const getCurrentProduct = useCallback(() => {
    const fallback = productCode ?? "Equity";

    // For known non-equity products (SBR, SBP, SB), always use productCode
    // regardless of what Pega messages may have set
    if (fallback === "SBR" || fallback === "SBP" || fallback === "SB") {
      return userData.tradingType && userData.tradingType !== "Equity" ? userData.tradingType : fallback;
    }

    if (userData.tradingType) {
      return userData.tradingType;
    }

    if (product?.toUpperCase() === "CFD") {
      return "CFD";
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlProduct = urlParams.get('product');
      if (urlProduct?.toUpperCase() === "CFD") {
        return "CFD";
      }
    } catch {
      // Ignore URL parsing errors
    }

    return fallback;
  }, [productCode, product]);

  // Registration events
  const logRegistrationStart = useCallback((email?: string) => {
    if (email) userData = { ...userData, email };
    const tradingType = getCurrentProduct();
    sendEvent('Registration', buildPayload('registration', 'signupEmail', { 'application.trading_type': tradingType, 'application.trader_type': traderType }));
  }, [buildPayload, getCurrentProduct, sendEvent, traderType]);

  const logRegistrationPassword = useCallback(() => {
    const tradingType = getCurrentProduct();
    sendEvent('Registration', buildPayload('registration', 'signupPassword', { 'application.trading_type': tradingType, 'application.trader_type': traderType }));
  }, [buildPayload, getCurrentProduct, sendEvent, traderType]);

  const logRegistrationVerificationCode = useCallback(() => {
    const tradingType = getCurrentProduct();
    sendEvent('Registration', buildPayload('registration', 'signupVerificationCode', { 'application.trading_type': tradingType, 'application.trader_type': traderType }));
  }, [buildPayload, getCurrentProduct, sendEvent, traderType]);

  const logRegistrationForgotPassword = useCallback(() => {
    const tradingType = getCurrentProduct();
    sendEvent('Registration', buildPayload('registration', 'signupForgotPasswordVerificationCode', { 'application.trading_type': tradingType, 'application.trader_type': traderType }));
  }, [buildPayload, getCurrentProduct, sendEvent, traderType]);

  const logRegistrationVerified = useCallback(() => {
    const tradingType = getCurrentProduct();
    sendEvent('Registration', buildPayload('registration', 'signupVerifiedNewUser', { 'application.trading_type': tradingType, 'application.trader_type': traderType }));

    if (demoRegistrationFallbackTimeout) clearTimeout(demoRegistrationFallbackTimeout);
  }, [buildPayload, getCurrentProduct, sendEvent, traderType]);

  const logVerificationFailed = useCallback(() => {
    const tradingType = getCurrentProduct();
    const mobilePayload = {
      EventAction: 'WrongCode',
      item_name: 'signupVerificationCode',
      ProductType: tradingType,
      Language: lang || '',
      CountryOfResidence: region || '',
      IntroducerID: '',
      PersonID: userData.personId || '',
      CustomerID: '',
      AccountID: '',
      TradingIntent: '',
      EventLabel: '',
      Environment: (import.meta.env.VITE_CUSTOM_ENV as string) === 'prod' ? 'production' : 'preprod',
      Tag: 'GoogleAnalytics',
      PageURL: window.location.href.substring(0, 100) || '',
      AccountFunction: isDemo ? 'Demo' : 'Live',
      CustomerType: '',
      ApplicationID: '',
    };
    dispatchMobileEvent('event', mobilePayload, embedded);
  }, [getCurrentProduct, lang, region, embedded, isDemo]);

  const logEmailAlreadyExists = useCallback(() => {
    const tradingType = getCurrentProduct();
    sendEvent('Registration', buildPayload('registration', 'signupExistingUserPassword', { 'application.trading_type': tradingType, 'application.trader_type': traderType }));
  }, [buildPayload, getCurrentProduct, sendEvent, traderType]);

  // App step events - fires "Live App Step" or "Demo App Step" based on journey context
  const createAppStepLogger = useCallback((step: string, includeAddressFinder = false, embedded?: boolean, embeddedValue?: string) => {
    return () => {
      const tradingType = getCurrentProduct();
      const eventName = isDemo ? 'Demo App Step' : 'Live App Step';
      const category = isDemo ? 'demo_application' : 'live_application';
      const extra: Record<string, unknown> = {
        'application.trader_type': traderType,
        'application.trading_type': tradingType,
        'application.step': embedded ? embeddedValue : step,
        'application.account_id': userData.demoAccountID || '',
      };
      if (includeAddressFinder) {
        extra['application.usedAddressFinder'] = userData.usedAddressFinder || false;
      }
      sendEvent(eventName, buildPayload(category, step, extra));
    };
  }, [buildPayload, getCurrentProduct, sendEvent, isDemo, traderType]);

const liveAppLoggers = useMemo(() => {
  return {
    logAccountPreferences: createAppStepLogger('Account preferences'),
    logSignUp: createAppStepLogger('personalDetails'),
    logApplyForALiveAccount: createAppStepLogger('Apply for a live account'),
    logSelectProductsForYourApplication: createAppStepLogger('Select products for your application', false, embedded, 'Add Product Via Hub'),
    logElectiveProfessionalAssessment: createAppStepLogger('electiveProfessionalAssessment'),
    logElectiveProfessionalFailure: createAppStepLogger('electiveProAssessmentFail'),
    logPersonalDetails: createAppStepLogger('PersonalDetails_2'),
    logFinancialDetails: createAppStepLogger('FinancialBackground'),
    logAdditionalPersonalDetails: createAppStepLogger('AdditionalPersonalDetails'),
    logNationalID: createAppStepLogger('identificationDocument'),
    logAddressDetails: createAppStepLogger('addressDetails', true),
    logEmploymentDetails: createAppStepLogger('employmentDetails'),
    logSourceOfFunds: createAppStepLogger('SourceOfFunds'),
    logAcknowledgementsAndDeclarations: createAppStepLogger('AcknowledgementsAndDeclarations'),
    logThankYouScreen: createAppStepLogger('ThankYouScreen'),
    logKnowledgeAndExperience: createAppStepLogger('KnowledgeAndExperience'),
    logCFDPricingOption: createAppStepLogger('CFDPricingOption'),
    logFeaturesAndRisks: createAppStepLogger('FeaturesAndRisks'),
    logYourDetails: createAppStepLogger('YourDetails'),
    logChooseYourAccount: createAppStepLogger('ChooseYourAccount'),
    logChooseYourProduct: createAppStepLogger('ChooseYourProduct'),
  };
}, [createAppStepLogger, embedded]);

  const logLiveAppSubmit = useCallback(() => {
    // Demo journeys should not fire "Live App Submit" - only fire for live context
    if (isDemo) return;
    const tradingType = getCurrentProduct();
    sendEvent('Live App Submit', buildPayload('live_application', 'CompletionsDetails', {
      'application.trader_type': 'live',
      'application.trading_type': tradingType,
      'application.usedAddressFinder': userData.usedAddressFinder || false,
      'application.id': userData.applicationID || '',
      'application.account_id': ''
    }));

    const mobilePayload = {
      EventAction: 'liveApplicationCompleted',
      item_name: 'applicationReceived',
      ProductType: tradingType,
      Language: lang || '',
      CountryOfResidence: region || '',
      IntroducerID: '',
      PersonID: userData.personId || '',
      CustomerID: '',
      AccountID: '',
      TradingIntent: '',
      EventLabel: '',
      Environment: (import.meta.env.VITE_CUSTOM_ENV as string) === 'prod' ? 'production' : 'preprod',
      Tag: 'GoogleAnalytics',
      PageURL: window.location.href.substring(0, 100) || '',
      AccountFunction: 'Live',
      CustomerType: 'Individual',
      ApplicationID: userData.applicationID || '',
    };
    dispatchMobileEvent('liveApplicationCompleted', mobilePayload, embedded);
  }, [buildPayload, getCurrentProduct, sendEvent, isDemo, lang, region, embedded]);

  // Subscribe to demo account creation (fires once on mount)
  useEffect(() => {
    const subscription = subscribeToPCoreMessage<{ demoAccountID?: string; caseID?: string; personID?: string }>(
      'CMC_DEMO_ACCOUNT_CREATED',
      (event) => {
        const { demoAccountID, caseID, personID } = event.message;
        if (demoAccountID && caseID && personID && !demoAppSubmitLoggedForPersonId) {
          demoAppSubmitLoggedForPersonId = personID;

          if (demoRegistrationFallbackTimeout) {
            clearTimeout(demoRegistrationFallbackTimeout);
            demoRegistrationFallbackTimeout = null;
          }
          userData = { ...userData, demoAccountID, caseID, personId: personID };
          const fireEvent = () => {
            const tradingType = getCurrentProduct();
            sendEvent('Demo App Submit', buildPayload('individual', 'Demo App Submit', {
              'application.trader_type': 'demo',
              'application.trading_type': tradingType,
              'application.account_id': demoAccountID,
              'application.case_id': caseID,
              'application.person_id': personID,
              'application.hosting_type': hostingType,
            }));

            const mobilePayload = {
              EventAction: 'accountRegistered',
              item_name: 'Demo App Submit',
              ProductType: tradingType,
              Language: lang || '',
              CountryOfResidence: region || '',
              IntroducerID: '',
              PersonID: personID,
              CustomerID: '',
              AccountID: demoAccountID,
              TradingIntent: context || '',
              EventLabel: '',
              Environment: (import.meta.env.VITE_CUSTOM_ENV as string) === 'prod' ? 'production' : 'preprod',
              Tag: 'GoogleAnalytics',
              PageURL: window.location.href.substring(0, 100) || '',
              AccountFunction: 'Demo',
              CustomerType: '',
              ApplicationID: userData.applicationID || '',
            };
            dispatchMobileEvent('event', mobilePayload, embedded);
          };

          try {
            if (window?.mParticle?.Identity && personID !== lastIdentifiedPersonId) {
              lastIdentifiedPersonId = personID;
              let eventFired = false;
              const safeFireEvent = () => {
                if (!eventFired) {
                  eventFired = true;
                  fireEvent();
                }
              };
              const fallbackTimeout = setTimeout(safeFireEvent, 3000);
              window.mParticle.Identity.identify(
                { userIdentities: { customerid: personID } },
                () => {
                  clearTimeout(fallbackTimeout);
                  safeFireEvent();
                }
              );
            } else {
              fireEvent();
            }
          } catch {
            fireEvent();
          }
        }
      }
    );

    return () => {
      if (subscription && typeof (subscription).unsubscribe === 'function') {
        (subscription).unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to PCore messaging service for application creation using matcher/criteria
  useEffect(() => {
    const subscription = subscribeToPCoreMessage<{ applicationID?: string; caseID?: string }>(
      'CMC_CRM_APPLICATION_CREATED',
      (event) => {
        const { applicationID, caseID } = event.message;
        if (applicationID) {
          userData.applicationID = applicationID;
          if (caseID) userData.caseID = caseID;
          if (applicationIDDeferred) {
            try {
              applicationIDDeferred.resolve(applicationID);
              if (applicationIDDeferred.timeoutId) clearTimeout(applicationIDDeferred.timeoutId);
            } finally {
              applicationIDDeferred = null;
            }
          }
        }
      }
    );

    return () => {
      if (subscription && typeof (subscription).unsubscribe === 'function') {
        (subscription).unsubscribe();
      }
    };
  }, []);

  return {
    setUserData,
    setTradingType,
    setUsedAddressFinder,
    logRegistrationStart,
    logRegistrationPassword,
    logRegistrationForgotPassword,
    logRegistrationVerificationCode,
    logRegistrationVerified,
    logVerificationFailed,
    logEmailAlreadyExists,
    ...liveAppLoggers,
    logLiveAppSubmit,
  };
};

// ============================================================================
// Global Types
// ============================================================================

declare global {
  interface Window {
    mParticle?: {
      ready(callback: () => void): void;
      getInstance(instanceName: string): {
        logEvent(eventName: string, eventType: number, eventData: Record<string, unknown>): void;
      };
      EventType: { Other: number; Navigation: number };
      Identity?: {
        identify(
          identityRequest: { userIdentities: { customerid: string; email?: string } },
          callback?: (result: MParticleIdentityResult) => void
        ): void;
      };
    };
    mParticleInstances?: string[];
    AnalyticsWebInterface?: {
      logEvent(name: string, params: string): void;
    };
    webkit?: {
      messageHandlers?: {
        firebase?: {
          postMessage(message: { command: string; name: string; parameters: Record<string, unknown> }): void;
        };
        CustomerHub?: {
          postMessage(message: { type: string }): void;
        };
      };
    };
  }
}

